import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import apiClient from '../../../../utils/apiClient';
import getTranslationLanguageLabel from '../../../../utils/translation-language-label';

const parseAssignments = (value) => {
  if (Array.isArray(value)) return value;
  if (!value || typeof value !== 'string') return [];

  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [parsed];
  } catch {
    try {
      return JSON.parse(`[${value}]`);
    } catch {
      return [];
    }
  }
};

const uniqueOptions = (options) => (
  [...new Map(options.filter(({ code }) => code).map((option) => [option.code, option])).values()]
);

const useAvailableLanguages = ({
  areaCodes = [],
  assignments,
  enabled = true,
}) => {
  const { t } = useTranslation();
  const [languages, setLanguages] = useState([]);
  const [error, setError] = useState(false);
  const assignmentsProvided = assignments !== undefined && assignments !== null;
  const normalizedAssignments = useMemo(() => parseAssignments(assignments), [assignments]);
  const normalizedAreaCodes = useMemo(() => {
    const assignmentCountries = normalizedAssignments
      .map(({ country, area_code }) => country || area_code)
      .filter(Boolean);
    return [...new Set([...areaCodes, ...assignmentCountries].filter(Boolean))];
    // A serialized key prevents callers' array identity from retriggering requests.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(areaCodes), normalizedAssignments]);

  useEffect(() => {
    if (!enabled) {
      setLanguages([]);
      setError(false);
      return;
    }

    const assignedOptions = uniqueOptions(normalizedAssignments.map((item) => {
      const code = typeof item === 'string' ? item : item.language;
      const country = typeof item === 'string' ? '' : (item.country || item.area_code);
      return {
        code,
        name: getTranslationLanguageLabel(code, country, t),
      };
    }));

    if (assignmentsProvided) {
      setLanguages(assignedOptions);
      setError(assignedOptions.length === 0);
      return;
    }

    if (normalizedAreaCodes.length === 0) {
      setLanguages([]);
      setError(false);
      return;
    }

    let active = true;
    const fetchLanguages = async () => {
      const responses = await Promise.allSettled(
        normalizedAreaCodes.map((areaCode) => apiClient.get('/hac/languages', {
          params: { area_code: areaCode },
        })),
      );
      if (!active) return;

      const options = responses.flatMap((result) => {
        if (result.status !== 'fulfilled' || !Array.isArray(result.value.data)) return [];
        return result.value.data.flatMap((country) => {
          const codes = Array.isArray(country.language)
            ? country.language
            : String(country.language || '').split(',').map((code) => code.trim());
          return codes.map((code) => ({
            code,
            name: getTranslationLanguageLabel(code, country.area_code, t),
          }));
        });
      });

      const uniqueLanguages = uniqueOptions(options);
      setLanguages(uniqueLanguages);
      setError(uniqueLanguages.length === 0);
    };

    fetchLanguages();
    return () => {
      active = false;
    };
  }, [assignmentsProvided, enabled, normalizedAreaCodes, normalizedAssignments, t]);

  return { languages, error };
};

export default useAvailableLanguages;
